import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';
import {PopoverInfo} from 'shells/popover-info/popover-info';
import {PageTabs} from 'shells/tabs/page-tabs/page-tabs';

import type {SalesDocumentVariant} from 'nutshell-graphql-types';
import type {WidgetFragment, Lead} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../../refetch-helpers';
import {useGetInvoices} from '../../../sales-documents/invoices/graphql/hooks';
import {useGetQuotes} from '../../../sales-documents/quotes/graphql/hooks';
import {useCreateSalesDocument} from '../../../sales-documents/shared/graphql/hooks';

import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {useUpdateWidgetCollapseState} from '../../../../core/card/graphql/hooks';

import {useGetMarketForLead} from '../graphql/hooks/use-get-market-for-lead';
import {useGetProductsForLead} from '../graphql/hooks/use-get-products-for-lead';
import {useUpdateLeadValue} from '../graphql/hooks/use-update-lead-value';

import {Header} from './header';
import {
    getTabOptions,
    getAddButtonProps,
    handleSalesDocumentChange,
    getIsLoading,
    getIsEmpty,
    getEmptyStateProps,
} from './helpers';
import {MarketSelector} from './market-selector';
import {ProductTab} from './product-tab';
import {RevenueWidgetOnboardingInfo} from './revenue-widget-onboarding-info';
import {SalesDocumentsTab} from './sales-documents-tab';

type Props = {
    lead: Lead;
    widget: WidgetFragment;
    canUseSalesDocuments: boolean;
};

export type RevenueTab = 'products' | 'quotes' | 'invoices';

export function RevenueWidget(props: Props) {
    const [selectedTab, setSelectedTab] = React.useState<RevenueTab>('products');
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {updateWidgetCollapseState} = useUpdateWidgetCollapseState();
    const {updateLeadValue} = useUpdateLeadValue();

    // Product tab
    const [isAddingProduct, setIsAddingProduct] = React.useState<boolean>(false);
    const {products, isFirstLoad: isFirstLoadProducts} = useGetProductsForLead(props.lead.id);
    const {market} = useGetMarketForLead(props.lead.id);

    const productCount = products ? products.edges.length : 0;

    // Sales documents tab
    const {
        isFirstLoad: isFirstLoadQuotes,
        quotes,
        refetch: refetchQuotes,
    } = useGetQuotes(props.lead.id);
    const {
        isFirstLoad: isFirstLoadInvoices,
        invoices,
        refetch: refetchInvoices,
    } = useGetInvoices(props.lead.id);
    const {createSalesDocument} = useCreateSalesDocument();

    const handleCreateSalesDocument = (salesDocumentVariant: SalesDocumentVariant) => {
        createSalesDocument({
            leadId: props.lead.id,
            salesDocumentVariant: salesDocumentVariant,
        })
            .then((response) => {
                const salesDocumentRes =
                    response && response.data && response.data.salesDocumentCreate;
                if (salesDocumentRes) {
                    handleSalesDocumentChange(
                        salesDocumentRes.salesDocument.modalUrl,
                        salesDocumentVariant === 'QUOTE' ? refetchQuotes : refetchInvoices
                    );
                }
            })
            .catch(() => {
                setError(
                    `Error creating ${salesDocumentVariant.toLowerCase()}, please refresh the page and try again.`
                );
            });
    };

    const isLoading = React.useMemo(
        () =>
            getIsLoading(selectedTab, isFirstLoadProducts, isFirstLoadQuotes, isFirstLoadInvoices),
        [selectedTab, isFirstLoadQuotes, isFirstLoadInvoices, isFirstLoadProducts]
    );

    const emptyStateProps = React.useMemo(() => {
        const quotesCount = quotes ? quotes.length : 0;
        const invoicesCount = invoices ? invoices.length : 0;
        const isEmpty = getIsEmpty(
            selectedTab,
            productCount,
            quotesCount,
            invoicesCount,
            isAddingProduct
        );

        return getEmptyStateProps(selectedTab, isEmpty);
    }, [selectedTab, productCount, quotes, invoices, isAddingProduct]);

    const handleTabChange = React.useCallback(
        (tab: RevenueTab) => {
            setSelectedTab(tab);
            if (isAddingProduct && tab !== 'products') {
                setIsAddingProduct(false);
            }
        },
        [isAddingProduct]
    );

    const handleUpdateLeadValue = React.useCallback(
        (value: number) => {
            // Whenever we update the lead value in this way, we always set the
            // value mode to MANUAL
            updateLeadValue({
                lead: props.lead.id,
                mode: 'MANUAL',
                value: value.toString(),
            })
                .then(() => {
                    refetchForEvent('valueUpdated');
                })
                .catch((e) => {
                    setError(e.message);
                });
        },
        [props.lead.id, updateLeadValue]
    );

    const handleCollapsedTabClick = React.useCallback(
        (tab: RevenueTab) => {
            handleTabChange(tab);
            updateWidgetCollapseState({
                widget: props.widget.id,
                isCollapsed: false,
            });
        },
        [updateWidgetCollapseState, props.widget.id, handleTabChange]
    );

    const hasOverdueInvoice = React.useMemo(
        () => Boolean(invoices.some((invoice) => invoice.overdueDuration !== null)),
        [invoices]
    );

    const tabProps = React.useMemo(
        () =>
            getTabOptions(
                selectedTab,
                handleTabChange,
                productCount,
                quotes.length,
                invoices.length,
                hasOverdueInvoice,
                props.canUseSalesDocuments
            ),
        [
            selectedTab,
            productCount,
            quotes.length,
            invoices.length,
            hasOverdueInvoice,
            props.canUseSalesDocuments,
            handleTabChange,
        ]
    );

    const collapsedTabs = React.useMemo(
        () =>
            tabProps.options.map((option) => ({
                id: option.id,
                label: option.label,
                count: option.count,
                isActive: false,
                badgeColor: option.badgeColor || undefined,
                // @ts-expect-error
                onClick: () => handleCollapsedTabClick(option.id),
            })),
        [tabProps.options, handleCollapsedTabClick]
    );

    const handleLeadProductModeChange = React.useCallback(() => {
        updateLeadValue({
            lead: props.lead.id,
            mode: 'PRODUCT',
        })
            .then(() => {
                refetchForEvent('valueUpdated');
            })
            .catch((e) => {
                setError(e.message);
            });
    }, [props.lead.id, updateLeadValue]);

    const headerContent = React.useMemo(() => {
        const productTotalValue = props.lead.products && props.lead.products.totalValue;
        const formattedValue = productTotalValue && productTotalValue.formatted;

        if (!formattedValue) {
            return null;
        }

        const valueNumber = props.lead.value ? props.lead.value.amount : 0;
        const leadCurrency = props.lead.value && props.lead.value.currency;

        if (!leadCurrency) {
            return null;
        }

        return (
            <Header
                valueNumber={valueNumber}
                valueString={formattedValue}
                valueMode={props.lead.valueMode}
                onSetProductValueMode={handleLeadProductModeChange}
                onUpdateLeadValue={handleUpdateLeadValue}
                leadCurrency={leadCurrency}
            />
        );
    }, [
        props.lead.products,
        props.lead.value,
        props.lead.valueMode,
        handleLeadProductModeChange,
        handleUpdateLeadValue,
    ]);

    const widgetSecondaryTitle = React.useMemo(() => {
        return (
            <Stack isHorizontal={true} spacing={8}>
                <PopoverInfo
                    info={<RevenueWidgetOnboardingInfo />}
                    noIconPadding={true}
                    shouldFadeIn={true}
                />
                <MarketSelector leadId={props.lead.id} setError={setError} />
            </Stack>
        );
    }, [props.lead.id, setError]);

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                isLoading={isLoading}
                secondaryTitle={widgetSecondaryTitle}
                addButtonProps={getAddButtonProps(
                    handleTabChange,
                    handleCreateSalesDocument,
                    setIsAddingProduct,
                    props.canUseSalesDocuments
                )}
                emptyStateProps={emptyStateProps}
                errorProps={{
                    error: error,
                    setError: setError,
                }}
                hasBorder={true}
                tabProps={{
                    tabs: tabProps,
                    headerContent: headerContent,
                }}
                collapsedContent={
                    <div className='px-12'>
                        <PageTabs tabs={collapsedTabs} lessPadding={true} />
                    </div>
                }
                setIsAdding={setIsAddingProduct}
            >
                <>
                    {selectedTab === 'products' && (
                        <ProductTab
                            leadId={props.lead.id}
                            products={products}
                            isAddingProduct={isAddingProduct}
                            setIsAddingProduct={setIsAddingProduct}
                            setError={setError}
                            market={market}
                        />
                    )}
                    {(selectedTab === 'quotes' || selectedTab === 'invoices') && (
                        <SalesDocumentsTab
                            tab={selectedTab}
                            setTab={setSelectedTab}
                            leadId={props.lead.id}
                            quotes={quotes}
                            invoices={invoices}
                            refetchQuotes={refetchQuotes}
                            refetchInvoices={refetchInvoices}
                        />
                    )}
                </>
            </CollapsibleCardWidget>
        </>
    );
}
